/**
 * 物料超领申请弹窗
 * 用于创建物料超领申请单，支持多个超领明细项（每个需求项可指定超领数量、原因类型等）
 */
import { FC, useEffect, useState } from "react";
import {
  Modal,
  Form,
  Button,
  InputNumber,
  Input,
  notification,
  Select,
  Tabs,
  Flex,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  AddMoiRequest,
  AddMoiRequestItem,
  OverIssueReasonTypeEnum,
  ReimbursRequest,
} from "@/types/reimburse";
import { useTranslation } from "react-i18next";
import { SConModal } from "@/styles/SConModal";
import { applicationSubmit, moiSave } from "@/apis/reimburse";
import RequirementSelect from "./RequirementSelect";
import errorCodes from "@/apis/error";

/** 组件 Props */
interface AddMaterialOverIssueProps {
  open: boolean;
  onSuccess: (data: ReimbursRequest) => void;
  onClose: () => void;
  /** 关联需求单 ID：从需求清单入口传入时，预填并以只读方式展示该需求单 */
  requirementId?: string;
  /** 关联需求单编号：requirementId 存在时用于只读展示（如需求单编号） */
  requirementNumber?: string;
  /** 预选的明细项：从需求详情入口按明细项发起时，作为首行预填 */
  initialItem?: { requirementItemId: string; productId?: string };
}

/** 每个 Tab 页签的激活状态 */
interface ItemTabState {
  [key: number]: string;
}

/** 单个超领明细表单项 */
interface AddMaterialOverIssueFormItem {
  requirementItemId?: string;
  productId?: string;
  overCount?: number;
  reasonType?: OverIssueReasonTypeEnum;
  reason?: string;
}

/** 超领申请完整表单值 */
interface AddMaterialOverIssueFormValues {
  title: string;
  content?: string;
  requirementId?: string;
  items?: AddMaterialOverIssueFormItem[];
}

const AddMaterialOverIssue: FC<AddMaterialOverIssueProps> = ({
  open,
  onSuccess,
  onClose,
  requirementId,
  requirementNumber,
  initialItem,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<"ADD" | "SUBMIT" | undefined>(
    undefined,
  );
  // 监听表单字段变化，用于联动逻辑
  const watchedRequirementId = Form.useWatch("requirementId", form);
  const watchedItems = Form.useWatch("items", form) || [];

  // 弹窗打开时重置表单
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldValue("items", [{}]); // 默认显示一个空项
      // 从需求清单入口传入 requirementId 时，预填并锁定关联需求单
      if (requirementId) {
        form.setFieldValue("requirementId", requirementId);
      }
    }
  }, [open, form, requirementId]);

  /**
   * 提交表单（保存 or 保存并提交）
   * @param isSubmit - true 为保存并提交，false 为仅保存
   */
  const handleSubmit = async (isSubmit: boolean) => {
    setLoading(isSubmit ? "SUBMIT" : "ADD");

    try {
      const values =
        (await form.validateFields()) as AddMaterialOverIssueFormValues;

      // 校验：必须选择需求单
      if (!values.requirementId) {
        notification.error({
          placement: "top",
          message: t("common.tip"),
          description: `${t("common.plSelect")}${t("layout.requirementList")}`,
        });
        return;
      }

      // 校验：至少一个超领明细项
      const items = Array.isArray(values.items) ? values.items : [];
      if (items.length === 0) {
        notification.error({
          placement: "top",
          message: t("common.tip"),
          description: `${t("common.plSelect")}${t("reimburse.requirementItemId")}`,
        });
        return;
      }

      // 校验：每个明细项必须选择需求项和产品
      const invalidItem = items.find(
        (item) => !item?.requirementItemId || !item?.productId,
      );
      if (invalidItem) {
        notification.error({
          placement: "top",
          message: t("common.tip"),
          description: t("reimburse.plSelectProducts"),
        });
        return;
      }

      // 组装请求数据
      const requestItems: AddMoiRequestItem[] = items.map((item) => ({
        requirementItemId: item.requirementItemId!,
        productId: item.productId!,
        overCount: item.overCount!,
        reasonType: item.reasonType!,
        reason: item.reason || "",
      }));

      const params: AddMoiRequest = {
        id: null,
        title: values.title,
        content: values.content || "",
        requirementId: values.requirementId,
        items: requestItems,
      };

      // 先保存，若需提交则再调提交接口
      let result = await moiSave(params);
      if (isSubmit) result = await applicationSubmit(false, result.id);
      notification.success({
        placement: "top",
        message: isSubmit
          ? t("reimburse.submitSuccess")
          : t("common.addSuccess"),
      });
      onSuccess(result);
    } catch (error: any) {
      notification.error({
        placement: "top",
        message: errorCodes[error] || t("common.operationFailed"),
        description: t("common.pleaseTryAgain"),
      });
    } finally {
      setLoading(undefined);
    }
  };

  /** 取消操作：重置表单并关闭弹窗 */
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 设置指定明细项的产品 ID
   * 当 RequirementSelect 选中需求项时，自动回填对应的产品信息
   */
  const handleSetRequirementItemProductId = (
    itemIndex: number,
    productId?: string,
  ) => {
    const items = form.getFieldValue("items") || [];
    items[itemIndex] = {
      ...items[itemIndex],
      productId,
    };
    form.setFieldValue("items", items);
  };

  // 切换需求单时，清空所有已选的需求项和产品（因为不同需求单的需求项不同）
  useEffect(() => {
    const currentItems = form.getFieldValue("items") || [];
    if (!Array.isArray(currentItems) || currentItems.length === 0) {
      return;
    }

    let hasChanges = false;
    const nextItems = currentItems.map((item) => {
      // 空项保持不变（新增的空行）
      if (!item?.requirementItemId && !item?.productId) {
        return item;
      }
      // 非空项重置为未选状态
      hasChanges = true;
      return {
        ...item,
        requirementItemId: undefined,
        productId: undefined,
      };
    });

    if (hasChanges) {
      form.setFieldValue("items", nextItems);
    }
  }, [form, watchedRequirementId]);

  // 从需求详情入口（initialItem 已传入）时，把该明细项作为首行预填。
  // 依赖 watchedRequirementId 以便在上方「切换需求单清空明细」effect 运行之后再落位，
  // 避免预填项被清空逻辑误清；仅在首行仍为空时补填，不覆盖用户已填内容。
  useEffect(() => {
    if (!open || !initialItem) return;
    const current = form.getFieldValue("items") || [];
    const first = current[0];
    const firstEmpty = !first?.requirementItemId && !first?.productId;
    if (firstEmpty) {
      form.setFieldValue("items", [
        {
          requirementItemId: initialItem.requirementItemId,
          productId: initialItem.productId,
        },
        ...current.slice(1),
      ]);
    }
  }, [open, form, initialItem, watchedRequirementId]);

  return (
    <Modal
      title={`${t("reimburse.addApplication")} ${t("reimburse.materialOverIssue")}`}
      open={open}
      onCancel={handleCancel}
      maskClosable={false}
      destroyOnClose
      width={1000}
      footer={
        <>
          <Button onClick={handleCancel}>{t("common.cancel")}</Button>
          <Button
            disabled={loading && loading !== "ADD"}
            type="primary"
            onClick={() => handleSubmit(false)}
            loading={loading === "ADD"}
          >
            {t("common.save")}
          </Button>
          <Button
            disabled={loading && loading !== "SUBMIT"}
            type="link"
            onClick={() => handleSubmit(true)}
            loading={loading === "SUBMIT"}
          >
            {t("reimburse.submit")}
          </Button>
        </>
      }
    >
      <Form
        form={form}
        labelCol={{ style: { width: 100, textAlign: "right" } }}
      >
        {/* 申请标题 */}
        <Form.Item
          label={t("reimburse.title")}
          name="title"
          rules={[{ required: true, message: t("reimburse.plTitle") }]}
          style={{ marginBottom: 8 }}
        >
          <Input placeholder={t("reimburse.plTitle")} maxLength={200} />
        </Form.Item>

        {/* 申请内容 */}
        <Form.Item
          label={t("reimburse.content")}
          name="content"
          rules={[{ required: true, message: t("reimburse.plContent") }]}
          style={{ marginBottom: 8 }}
        >
          <Input.TextArea
            placeholder={t("reimburse.plContent")}
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={500}
          />
        </Form.Item>

        {/* 关联需求单：从需求清单入口（requirementId 已传入）时只读展示编号，否则可搜索选择。
            表单始终以 id 作为 requirementId 存储值，锁定时通过 getValueProps 把展示值映射为编号。 */}
        <Form.Item
          label={t("layout.requirementList")}
          name="requirementId"
          style={{ marginBottom: 8 }}
          {...(requirementId
            ? { getValueProps: () => ({ value: requirementNumber }) }
            : {})}
        >
          {requirementId ? (
            <Input disabled />
          ) : (
            <RequirementSelect
              entityType="requirement"
              placeholder={t("requirement.listSearchTip")}
            />
          )}
        </Form.Item>

        {/* 超领明细列表（动态增删） */}
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  style={{
                    border: "1px solid #b0aeae",
                    borderRadius: 6,
                    padding: "0px 16px 24px",
                    marginBottom: 8,
                  }}
                >
                  <Tabs
                    tabBarExtraContent={
                      // 至少保留一项，多于一项时显示删除按钮
                      fields.length > 1 && (
                        <SConModal
                          title={t("common.rmTip")}
                          content={t("common.rmContent")}
                          onOk={() => Promise.resolve(remove(name))}
                        >
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </SConModal>
                      )
                    }
                    items={[
                      {
                        key: "info",
                        label: t("common.basicInfo"),
                        children: (
                          <Flex vertical gap={8}>
                            {/* 需求项选择（联动产品回填 + 已选项排除） */}
                            <Form.Item
                              {...restField}
                              name={[name, "requirementItemId"]}
                              label={t("reimburse.requirementItemId")}
                              rules={[
                                {
                                  required: true,
                                  message: t("reimburse.requirementItemId"),
                                },
                              ]}
                              style={{ marginBottom: 0, flex: 1 }}
                            >
                              <RequirementSelect
                                entityType="item"
                                // 从需求详情入口预填的首行明细项只读
                                disabled={!!initialItem && name === 0}
                                // 排除其他明细项已选的需求项，防止重复选择
                                excludeIds={
                                  watchedItems
                                    .map(
                                      (
                                        item: { requirementItemId?: string },
                                        index: number,
                                      ) =>
                                        index === name
                                          ? undefined
                                          : item?.requirementItemId,
                                    )
                                    .filter(Boolean) as string[]
                                }
                                onChange={(value) => {
                                  form.setFieldValue(
                                    ["items", name, "requirementItemId"],
                                    value,
                                  );
                                  // 清空时同步清除关联的产品
                                  if (!value) {
                                    handleSetRequirementItemProductId(
                                      name,
                                      undefined,
                                    );
                                  }
                                }}
                                // 选中需求项后自动回填产品 ID
                                onEntityChange={(item) => {
                                  handleSetRequirementItemProductId(
                                    name,
                                    item?.productId,
                                  );
                                }}
                                placeholder={t("reimburse.requirementItemId")}
                                requirementId={watchedRequirementId}
                              />
                            </Form.Item>

                            {/* 超领数量（正整数） */}
                            <Form.Item
                              {...restField}
                              name={[name, "overCount"]}
                              label={t("reimburse.overCount")}
                              rules={[
                                {
                                  required: true,
                                  message: t("reimburse.overCount"),
                                },
                                {
                                  validator: (_, value) =>
                                    typeof value === "number" && value > 0
                                      ? Promise.resolve()
                                      : Promise.reject(
                                          new Error(t("reimburse.overCount")),
                                        ),
                                },
                              ]}
                              style={{ marginBottom: 0, flex: 1 }}
                            >
                              <InputNumber changeOnWheel
                                min={1}
                                precision={0}
                                style={{ width: "100%" }}
                                placeholder={t("reimburse.overCount")}
                              />
                            </Form.Item>

                            {/* 超领原因类型 */}
                            <Form.Item
                              {...restField}
                              name={[name, "reasonType"]}
                              label={t("reimburse.reasonType")}
                              rules={[
                                {
                                  required: true,
                                  message: t("reimburse.reasonType"),
                                },
                              ]}
                              style={{ marginBottom: 0, flex: 1 }}
                            >
                              <Select
                                placeholder={t("reimburse.reasonType")}
                                options={[
                                  {
                                    label: t("reimburse.reasonMistake"),
                                    value: OverIssueReasonTypeEnum.MISTAKE,
                                  },
                                  {
                                    label: t("reimburse.reasonWaste"),
                                    value: OverIssueReasonTypeEnum.WASTE,
                                  },
                                  {
                                    label: t("reimburse.reasonOther"),
                                    value: OverIssueReasonTypeEnum.OTHER,
                                  },
                                ]}
                              />
                            </Form.Item>

                            {/* 超领原因描述 */}
                            <Form.Item
                              {...restField}
                              name={[name, "reason"]}
                              label={t("reimburse.reason")}
                              style={{ marginBottom: 0, flex: 1 }}
                            >
                              <Input.TextArea
                                autoSize={{ minRows: 2, maxRows: 2 }}
                                showCount
                                placeholder={t("reimburse.reason")}
                                maxLength={500}
                              />
                            </Form.Item>
                          </Flex>
                        ),
                      },
                    ]}
                  />
                </div>
              ))}

              {/* 添加明细项按钮 */}
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="dashed"
                  onClick={() => {
                    add({});
                  }}
                  block
                  icon={<PlusOutlined />}
                >
                  {t("common.add")}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default AddMaterialOverIssue;
